// Shared billing helpers — mirror of the server's `computeBilling` so the
// front-desk UI can show a live total/preview while the operator types.
// Server stays the source of truth — this is purely for instant feedback.

export const BILLING_MODES = ["nightly", "hourly", "daily"];

// Standard hospitality fallback (used when nothing more specific applies).
export const DEFAULT_CHECK_IN_TIME  = "15:00";   // 3 PM
export const DEFAULT_CHECK_OUT_TIME = "12:00";   // Noon

// House convention for *nightly* stays: arrive in the evening, depart
// next morning.  StayWindowPicker locks these on so operators can pick
// the date and the count of nights without touching the time fields.
export const NIGHTLY_CHECK_IN_TIME  = "18:00";   // 6 PM
export const NIGHTLY_CHECK_OUT_TIME = "07:00";   // 7 AM next day

// Default duration used when a new booking is created in each mode.
export const DEFAULT_NIGHTS = 1;
export const DEFAULT_HOURS  = 2;
export const DEFAULT_DAYS   = 1;

// Parse a YYYY-MM-DDTHH:MM (datetime-local) or YYYY-MM-DD string into a Date.
export function parseStayDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T${DEFAULT_CHECK_IN_TIME}:00`);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return new Date(`${s}:00`);
  return new Date(s.replace(" ", "T"));
}

// Render a Date back into the datetime-local input format.
export function toDateTimeLocalValue(d) {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

// Default datetime for "today at the standard time".
export function defaultStartOfStay(time = DEFAULT_CHECK_IN_TIME) {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  return toDateTimeLocalValue(new Date(`${d.toISOString().slice(0,10)}T${time}:00`));
}
export function defaultEndOfStay(time = DEFAULT_CHECK_OUT_TIME) {
  const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1);
  return toDateTimeLocalValue(new Date(`${d.toISOString().slice(0,10)}T${time}:00`));
}

// ─── Mode-aware datetime helpers ──────────────────────────────────────────
// Used by <StayWindowPicker> so operators can pick a date / nights / hours
// without thinking about whether the resulting times are coherent.  Server
// stays the source of truth — these helpers just keep the UI's math the
// same as the server's.

// Strip the time portion off a datetime-local string ("YYYY-MM-DDTHH:mm").
// Returns the date in YYYY-MM-DD or "" if we can't parse it.
export function extractDate(dtLocal) {
  if (!dtLocal) return "";
  const s = String(dtLocal);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
}

// Round a Date up to the next half hour — used as the default check-in
// time for hourly bookings so "Add 30 min" lands on a clean :00 / :30.
export function roundToNextHalfHour(d = new Date()) {
  const out = new Date(d);
  out.setSeconds(0, 0);
  const mins = out.getMinutes();
  const add  = mins === 0 ? 0
             : mins <= 30 ? 30 - mins
             : 60 - mins;
  if (add > 0) out.setMinutes(out.getMinutes() + add);
  return out;
}

// Build a datetime-local string for a specific date + clock time.
// Date can be Date / "YYYY-MM-DD" / datetime-local string; time is "HH:mm".
export function dateAtTime(date, time = NIGHTLY_CHECK_IN_TIME) {
  const day = extractDate(typeof date === "string" ? date : toDateTimeLocalValue(date));
  if (!day) return "";
  return `${day}T${time}`;
}

// Add N nights to a check-in.  Nights anchor the clock by definition:
// the check-out is the morning AFTER the Nth night, at NIGHTLY_CHECK_OUT_TIME.
// e.g. 2026-06-15T18:00 + 1 night → 2026-06-16T07:00.
export function addNights(checkInDtLocal, nights = 1, outTime = NIGHTLY_CHECK_OUT_TIME) {
  const start = parseStayDateTime(checkInDtLocal);
  if (!start) return "";
  const out = new Date(start);
  out.setDate(out.getDate() + Math.max(1, Number(nights) || 1));
  const [hh, mm] = outTime.split(":").map((n) => Number(n) || 0);
  out.setHours(hh, mm, 0, 0);
  return toDateTimeLocalValue(out);
}

// Add hours (supports half-hour increments) to a check-in.
export function addHours(checkInDtLocal, hours = 1) {
  const start = parseStayDateTime(checkInDtLocal);
  if (!start) return "";
  const out = new Date(start.getTime() + Math.max(0.5, Number(hours) || 0) * 3600000);
  return toDateTimeLocalValue(out);
}

// Add N × 24 hours to a check-in — preserves time-of-day exactly so
// daily stays always begin and end at the same wall-clock time.
export function addDays(checkInDtLocal, days = 1) {
  const start = parseStayDateTime(checkInDtLocal);
  if (!start) return "";
  const out = new Date(start.getTime() + Math.max(1, Number(days) || 1) * 86400000);
  return toDateTimeLocalValue(out);
}

// Mode-aware "what should I pre-fill?".  Picker calls this when the
// mode changes (or first mounts with no value) so the operator never
// stares at a blank form.
export function defaultStartForMode(mode) {
  if (mode === "hourly") {
    return toDateTimeLocalValue(roundToNextHalfHour(new Date()));
  }
  if (mode === "daily") {
    // Snap to the next whole hour — keeps Daily stays starting at a
    // tidy time without imposing an arbitrary 3 PM convention.
    const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1);
    return toDateTimeLocalValue(d);
  }
  // nightly — today at the house convention (6 PM).
  return dateAtTime(new Date(), NIGHTLY_CHECK_IN_TIME);
}

// Computes the check-out from the check-in for a mode + duration.  This
// is what the picker's read-only "Depart" field actually displays.
export function endForMode(mode, startDtLocal, count) {
  if (mode === "hourly") return addHours(startDtLocal, count || DEFAULT_HOURS);
  if (mode === "daily")  return addDays  (startDtLocal, count || DEFAULT_DAYS);
  return addNights(startDtLocal, count || DEFAULT_NIGHTS);
}

// Reverse — how many nights / hours / days does this window represent?
// Used when the picker boots up with values restored from the server
// (e.g. an edit) so the count input shows the right number.
export function countForMode(mode, startDtLocal, endDtLocal) {
  const start = parseStayDateTime(startDtLocal);
  const end   = parseStayDateTime(endDtLocal);
  if (!start || !end || end.getTime() <= start.getTime()) {
    if (mode === "hourly") return DEFAULT_HOURS;
    if (mode === "daily")  return DEFAULT_DAYS;
    return DEFAULT_NIGHTS;
  }
  if (mode === "hourly") {
    return Math.max(0.5, Math.round(((end - start) / 3600000) * 2) / 2);
  }
  if (mode === "daily") {
    return Math.max(1, Math.round((end - start) / 86400000));
  }
  // nightly — count the midnights between (server's definition).
  const a = new Date(start); a.setHours(0, 0, 0, 0);
  const b = new Date(end);   b.setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((b - a) / 86400000));
}

// Same midnight-counting logic the server uses for nightly stays.
function nightsBetweenDt(inDt, outDt) {
  const a = new Date(inDt);  a.setHours(0, 0, 0, 0);
  const b = new Date(outDt); b.setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000));
}
function daysBetweenDt(inDt, outDt) {
  const h = (outDt.getTime() - inDt.getTime()) / 3600000;
  return Math.max(1, Math.ceil(h / 24));
}
function hoursBetweenDt(inDt, outDt) {
  const h = (outDt.getTime() - inDt.getTime()) / 3600000;
  return Math.max(0.5, Math.ceil(h * 2) / 2);
}

// Returns { ok, mode, nights, hours, rate, total, label } where `label`
// is the human-readable summary ("3 nights × GH₵ 200").
//
// Mirror of server `computeBilling` — daily mode uses its own per-day
// rate; if the operator hasn't set one yet we fall back to the nightly
// rate so the preview still shows a sensible number.
export function computeBillingClient({
  billing_mode,
  check_in_at,
  check_out_at,
  rate_per_night,
  rate_per_hour,
  rate_per_day,
}) {
  const mode = BILLING_MODES.includes(billing_mode) ? billing_mode : "nightly";
  const inDt  = parseStayDateTime(check_in_at);
  const outDt = parseStayDateTime(check_out_at);
  if (!inDt || !outDt || isNaN(inDt) || isNaN(outDt)) {
    return { ok: false, mode, nights: 0, hours: 0, rate: 0, total: 0, label: "" };
  }
  if (outDt.getTime() <= inDt.getTime()) {
    return { ok: false, mode, nights: 0, hours: 0, rate: 0, total: 0, label: "Check-out before check-in" };
  }

  if (mode === "hourly") {
    const hours = hoursBetweenDt(inDt, outDt);
    const rate  = Math.max(0, Number(rate_per_hour) || 0);
    const total = Math.round(rate * hours * 100) / 100;
    return { ok: true, mode, nights: 0, hours, rate, total, label: `${hours} hr × GH₵ ${rate.toFixed(2)} = GH₵ ${total.toFixed(2)}` };
  }
  if (mode === "daily") {
    const days = daysBetweenDt(inDt, outDt);
    const day  = Number(rate_per_day);
    const rate = Math.max(0, day > 0 ? day : (Number(rate_per_night) || 0));
    const total = Math.round(rate * days * 100) / 100;
    return { ok: true, mode, nights: days, hours: 0, rate, total, label: `${days} day${days === 1 ? "" : "s"} × GH₵ ${rate.toFixed(2)} = GH₵ ${total.toFixed(2)}` };
  }
  const nights = nightsBetweenDt(inDt, outDt);
  const rate   = Math.max(0, Number(rate_per_night) || 0);
  const total  = Math.round(rate * nights * 100) / 100;
  return { ok: true, mode, nights, hours: 0, rate, total, label: `${nights} night${nights === 1 ? "" : "s"} × GH₵ ${rate.toFixed(2)} = GH₵ ${total.toFixed(2)}` };
}
